import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Play, Pause, Volume2, ChevronDown, X, RotateCcw, RotateCw, 
  Sparkles, VolumeX, Minus, Plus, Info
} from 'lucide-react';
import { SavedTestAlignSession, testAlignStorage } from '../services/testAlignStorage';
import { sanitizeHtml } from '../utils/sanitizeHtml';

interface TestAlignReaderPlayerProps {
  session: SavedTestAlignSession;
  onClose: () => void;
}

/**
 * Busca binária rápida para localizar o último parágrafo onde para[1] <= currentTime.
 */
export function binarySearchParasFloor(paras: Array<[string | number, number]>, currentTime: number): number {
  if (!paras || paras.length === 0) return 0;

  const getStartTime = (pair: [string | number, number]): number => {
    if (!pair) return 0;
    return typeof pair[1] === 'number' ? pair[1] : (parseFloat(String(pair[1])) || 0);
  };

  if (currentTime < getStartTime(paras[0])) return 0;

  let low = 0;
  let high = paras.length - 1;
  let ans = 0;

  while (low <= high) {
    const mid = (low + high) >> 1;
    if (getStartTime(paras[mid]) <= currentTime) {
      ans = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return ans;
}

/**
 * Busca binária rápida (floor) para localizar o índice da frase ativa em phraseBegins.
 */
export function binarySearchFloor(phraseBegins: number[], currentTime: number): number {
  if (!phraseBegins || phraseBegins.length === 0) return 0;
  if (currentTime < phraseBegins[0]) return 0;
  let low = 0;
  let high = phraseBegins.length - 1;
  let ans = 0;

  while (low <= high) {
    const mid = (low + high) >> 1;
    if (phraseBegins[mid] <= currentTime) {
      ans = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return ans;
}

/**
 * Mapeia o índice da frase para o índice do parágrafo contando a quantidade de zeros em phraseIndexes.
 */
export function getParagraphIndexFromPhraseIndex(phraseIndexes: number[], phraseIndex: number): number {
  if (!phraseIndexes || phraseIndexes.length === 0) return 0;
  const boundedIndex = Math.min(phraseIndex, phraseIndexes.length - 1);
  let zeroCount = 0;
  for (let i = 0; i <= boundedIndex; i++) {
    if (phraseIndexes[i] === 0) {
      zeroCount++;
    }
  }
  return Math.max(0, zeroCount - 1);
}

const formatTime = (seconds: number) => {
  if (isNaN(seconds) || seconds < 0) return '00:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }
  return `${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

export const TestAlignReaderPlayer: React.FC<TestAlignReaderPlayerProps> = ({ session, onClose }) => {
  const { alignment, htmlContent } = session;
  const { paras = [], phraseBegins = [], phraseIndexes = [] } = alignment;

  const [fontSize, setFontSize] = useState(19);
  const [showDiagnostics, setShowDiagnostics] = useState(true);

  // Audio & Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(session.savedTime || 0);
  const [duration, setDuration] = useState(session.alignment.total_duration_seconds || 0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  // Playback speeds: 1x -> 1.5x -> 2x -> 0.75x
  const speedCycle = [1, 1.5, 2, 0.75];
  const [speedIndex, setSpeedIndex] = useState(0);
  const currentSpeed = speedCycle[speedIndex];

  // Active sync pointers
  const [activePhraseIndex, setActivePhraseIndex] = useState(0);
  const [activeParaIndex, setActiveParaIndex] = useState(0);
  const [activeParaId, setActiveParaId] = useState<string>('');

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const readerContainerRef = useRef<HTMLDivElement | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const initialTimeSetRef = useRef(false);
  const lastActiveElementRef = useRef<HTMLElement | null>(null);

  // Mapeamento pré-calculado: cada frase em phraseIndexes aponta diretamente para o seu parágrafo em O(1)
  const phraseToParaMap = useMemo(() => {
    if (!phraseIndexes || phraseIndexes.length === 0) return [];
    const map = new Int32Array(phraseIndexes.length);
    let currentParaIdx = -1;
    for (let i = 0; i < phraseIndexes.length; i++) {
      if (phraseIndexes[i] === 0) {
        currentParaIdx++;
      }
      map[i] = Math.max(0, currentParaIdx);
    }
    return map;
  }, [phraseIndexes]);

  // Audio URL initialization
  useEffect(() => {
    if (session.audioBlob) {
      const url = URL.createObjectURL(session.audioBlob);
      setAudioUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (session.audioSrc) {
      setAudioUrl(session.audioSrc);
    }
  }, [session.audioBlob, session.audioSrc]);

  // Audio setup on URL change
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    if (!initialTimeSetRef.current) {
      audio.currentTime = session.savedTime || 0;
      initialTimeSetRef.current = true;
    }
    audio.playbackRate = currentSpeed;
    audio.volume = isMuted ? 0 : volume;

    const handleLoadedMetadata = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [audioUrl]);

  // Speed sync
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = currentSpeed;
    }
  }, [currentSpeed]);

  // Volume sync
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Process HTML: remover links CSS relativos (elimina erros MIME) e assegurar classes/dataset
  const processedHtml = useMemo(() => {
    if (!htmlContent) return '';
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');

      // 1. Remover todos os links de CSS relativos/externos para eliminar erros de MIME type
      doc.querySelectorAll('link[rel="stylesheet"], link').forEach(link => {
        link.remove();
      });

      // 2. Correlacionar e assegurar atributos nos parágrafos narrativos se necessário
      const pElements = doc.querySelectorAll('p, div.paragraph, div.para, .audio-para-item');
      if (paras && paras.length > 0) {
        pElements.forEach((p, idx) => {
          // Ignorar blocos de metadados de apresentação
          if (p.closest('.tale-meta-intro-block') || p.classList.contains('tale-meta-intro-block') || p.classList.contains('audio-non-narrative')) {
            return;
          }

          // Assegurar classe .audio-para-item apenas se não for bloco neutro
          if (!p.classList.contains('audio-para-item')) {
            p.classList.add('audio-para-item');
          }
          // Assegurar id do parágrafo
          if (!p.id && paras[idx]) {
            p.id = String(paras[idx][0]);
          }
          // Assegurar data-para-idx apenas se não tiver sido pré-atribuído
          if (!p.getAttribute('data-para-idx')) {
            p.setAttribute('data-para-idx', String(idx));
          }
          // Assegurar data-para-start apenas se não tiver sido pré-atribuído
          if (!p.getAttribute('data-para-start') && paras[idx]) {
            const startVal = typeof paras[idx][1] === 'number' ? paras[idx][1] : parseFloat(String(paras[idx][1]));
            if (!isNaN(startVal)) {
              p.setAttribute('data-para-start', String(startVal));
            }
          }
        });
      }

      return doc.body.innerHTML || htmlContent;
    } catch {
      return htmlContent;
    }
  }, [htmlContent, paras]);

  /**
   * Sincronização em tempo real Tale.fyi:
   * 1. Utiliza phraseBegins para encontrar a frase ativa exata pelo currentTime
   * 2. Mapeia a qual parágrafo aquela frase pertence via phraseIndexes
   * 3. Localiza o parágrafo no DOM por ID e faz scroll e destaque fluído instantaneamente
   */
  const syncPlaybackToTime = (cur: number) => {
    let indice_frase = 0;
    let indice_paragrafo = 0;

    // 1. Utilizar o array contínuo phraseBegins (timestamps de cada frase) em vez de olhar apenas para o início do parágrafo
    // 2. Identificar a frase ativa em tempo real com base no currentTime do áudio
    if (phraseBegins && phraseBegins.length > 0) {
      indice_frase = binarySearchFloor(phraseBegins, cur);
      setActivePhraseIndex(indice_frase);

      // 3. Mapear qual parágrafo (phraseIndexes) aquela frase pertence
      if (phraseToParaMap.length > indice_frase) {
        indice_paragrafo = phraseToParaMap[indice_frase];
      } else {
        indice_paragrafo = getParagraphIndexFromPhraseIndex(phraseIndexes, indice_frase);
      }
    } else if (paras && paras.length > 0) {
      // Fallback caso phraseBegins não esteja presente
      indice_paragrafo = binarySearchParasFloor(paras, cur);
    }

    setActiveParaIndex(indice_paragrafo);

    // Obter ID do parágrafo em paras[indice_paragrafo][0]
    let id = '';
    if (paras && paras[indice_paragrafo]) {
      id = String(paras[indice_paragrafo][0]);
    }
    setActiveParaId(id);

    // 4. Localizar o elemento DOM do parágrafo e aplicar o scroll e destaque visual de forma fluida e instantânea
    const container = readerContainerRef.current;
    let elemento: HTMLElement | null = null;
    if (container) {
      // Prioridade 1: Buscar diretamente pelo data-para-idx atribuído ao parágrafo mapeado
      elemento = container.querySelector(`.audio-para-item[data-para-idx="${indice_paragrafo}"]`);

      // Prioridade 2: Buscar pelo ID do alinhamento se existir no DOM
      if (!elemento && id) {
        try {
          elemento = container.querySelector(`#${CSS.escape(id)}`) ||
                     container.querySelector(`.audio-para-item[id="${CSS.escape(id)}"]`);
        } catch {
          elemento = null;
        }
      }

      // Prioridade 3: Fallback por ID no documento
      if (!elemento && id) {
        elemento = document.getElementById(id);
      }

      // Prioridade 4: Fallback ordinal restrito apenas a elementos de áudio
      if (!elemento) {
        const allItems = container.querySelectorAll('.audio-para-item');
        if (allItems[indice_paragrafo]) {
          elemento = allItems[indice_paragrafo] as HTMLElement;
        }
      }
    }

    if (elemento && elemento !== lastActiveElementRef.current) {
      if (lastActiveElementRef.current) {
        lastActiveElementRef.current.classList.remove('audio-phrase-active');
      }
      elemento.classList.add('audio-phrase-active');
      lastActiveElementRef.current = elemento;

      elemento.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  // Evento timeupdate nativo do áudio
  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const cur = audio.currentTime;
    setCurrentTime(cur);
    syncPlaybackToTime(cur);

    // Salvar progresso
    testAlignStorage.updateProgress(session.id, cur);
  };

  // Loop com requestAnimationFrame enquanto toca para sincronismo cirúrgico sem latência
  useEffect(() => {
    let animId: number;

    const tick = () => {
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        const cur = audio.currentTime;
        setCurrentTime(cur);
        syncPlaybackToTime(cur);
      }
      if (isPlaying) {
        animId = requestAnimationFrame(tick);
      }
    };

    if (isPlaying) {
      animId = requestAnimationFrame(tick);
    }

    return () => {
      if (animId) cancelAnimationFrame(animId);
    };
  }, [isPlaying, phraseBegins, phraseIndexes, paras, phraseToParaMap]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const skipSeconds = (secs: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = Math.max(0, Math.min(duration || audio.duration || 99999, audio.currentTime + secs));
    audio.currentTime = newTime;
    setCurrentTime(newTime);
    syncPlaybackToTime(newTime);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setCurrentTime(val);
    if (audioRef.current) {
      audioRef.current.currentTime = val;
    }
    syncPlaybackToTime(val);
  };

  const cycleSpeed = () => {
    const nextIdx = (speedIndex + 1) % speedCycle.length;
    setSpeedIndex(nextIdx);
  };

  // Ajuste no Evento de Clique no Parágrafo:
  // Ao clicar em qualquer texto do leitor, NÃO use o índice geral de tags <p>. Use o atributo do próprio elemento clicado
  const onParagrafoClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const elemento = target.closest('.audio-para-item') as HTMLElement | null;
    if (!elemento) return;

    // Ignorar blocos sem áudio ou de apresentação
    if (elemento.classList.contains('audio-non-narrative') || elemento.closest('.tale-meta-intro-block')) {
      return;
    }

    // Pega o tempo real exato gravado no parágrafo
    let tempoInicio = parseFloat(elemento.dataset.paraStart || '');

    // Fallback caso data-para-start não esteja no dataset
    if (isNaN(tempoInicio)) {
      const idxStr = elemento.dataset.paraIdx;
      if (idxStr !== undefined && paras[parseInt(idxStr, 10)]) {
        tempoInicio = parseFloat(String(paras[parseInt(idxStr, 10)][1]));
      } else if (elemento.id) {
        const found = paras.find(p => String(p[0]) === elemento.id);
        if (found) {
          tempoInicio = parseFloat(String(found[1]));
        }
      }
    }

    const audio = audioRef.current;
    if (audio && !isNaN(tempoInicio)) {
      audio.currentTime = tempoInicio;
      setCurrentTime(tempoInicio);
      syncPlaybackToTime(tempoInicio);
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-slate-950/90 backdrop-blur-xl flex items-center justify-center p-2 sm:p-4 md:p-6 animate-in fade-in duration-300">
      {/* Inject specification CSS */}
      <style>{`
        .audio-phrase-active {
          background-color: rgba(234, 138, 38, 0.35) !important;
          border-radius: 6px;
          transition: background-color 0.2s ease;
        }
        .audio-para-item {
          cursor: pointer;
          border-radius: 6px;
          padding: 6px 10px;
          margin-bottom: 1.25rem;
          transition: background-color 0.2s ease;
        }
        .audio-para-item:hover {
          background-color: rgba(255, 255, 255, 0.04);
        }
        .tale-reader-content p {
          margin-bottom: 1.25rem;
          line-height: 1.8;
        }
      `}</style>

      {/* Hidden Audio Element */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
      )}

      {/* Player Modal Wrapper */}
      <div className="relative w-full max-w-4xl bg-slate-900/95 border border-white/10 rounded-[28px] p-4 sm:p-6 flex flex-col justify-between text-white max-h-[96vh] h-full overflow-hidden my-auto">
        {/* Top Header Bar */}
        <div className="flex items-center justify-between border-b border-white/10 pb-3 shrink-0 gap-2">
          <div className="flex items-center gap-3 truncate">
            <button
              onClick={onClose}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-2xl text-white/70 hover:text-white transition-colors shrink-0"
              title="Voltar / Fechar Teste"
            >
              <ChevronDown size={20} />
            </button>
            <div className="truncate">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/30 font-mono text-[9px] font-medium rounded uppercase">
                  TALE.FYI ENGINE
                </span>
                <h3 className="font-medium text-sm text-white truncate">{session.title}</h3>
              </div>
              <p className="text-[11px] text-white/60 truncate mt-0.5">{session.author} • {session.language.toUpperCase()}</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Font size adjustment */}
            <div className="flex items-center bg-white/10 rounded-2xl p-1 text-xs">
              <button
                onClick={() => setFontSize(Math.max(14, fontSize - 2))}
                className="p-1.5 hover:bg-white/20 rounded-xl transition-colors"
                title="Diminuir Fonte"
              >
                <Minus size={14} />
              </button>
              <span className="px-2 font-mono font-bold text-[11px]">{fontSize}px</span>
              <button
                onClick={() => setFontSize(Math.min(32, fontSize + 2))}
                className="p-1.5 hover:bg-white/20 rounded-xl transition-colors"
                title="Aumentar Fonte"
              >
                <Plus size={14} />
              </button>
            </div>

            {/* Diagnostics toggle */}
            <button
              onClick={() => setShowDiagnostics(!showDiagnostics)}
              className={`p-2 rounded-2xl transition-all flex items-center gap-1 text-xs font-bold ${
                showDiagnostics ? 'bg-amber-500 text-slate-950 shadow-md' : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
              title="Alternar Painel de Diagnóstico"
            >
              <Info size={16} />
              <span className="hidden sm:inline">Diagnóstico</span>
            </button>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 text-white/70 hover:text-rose-400 hover:bg-rose-950/30 rounded-2xl transition-colors"
              title="Fechar"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Live Diagnostics Banner */}
        {showDiagnostics && (
          <div className="mt-2 p-2.5 bg-slate-950/80 border border-amber-500/30 rounded-2xl shrink-0 font-mono text-[11px] text-slate-300 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <span className="text-slate-500 block text-[9px] uppercase font-sans font-bold">Parágrafo Ativo</span>
              <span className="text-sky-400 font-bold">#{activeParaIndex}</span> {activeParaId && <span className="text-amber-300">({activeParaId})</span>}
            </div>
            <div>
              <span className="text-slate-500 block text-[9px] uppercase font-sans font-bold">Frase Ativa (phraseBegins)</span>
              <span className="text-emerald-400 font-bold">#{activePhraseIndex}</span> <span className="text-slate-400 text-[10px]">({(phraseBegins[activePhraseIndex] ?? 0).toFixed(2)}s)</span>
            </div>
            <div>
              <span className="text-slate-500 block text-[9px] uppercase font-sans font-bold">Tempo do Áudio</span>
              <span className="text-amber-400 font-bold">{currentTime.toFixed(2)}s</span> / {(duration || 0).toFixed(1)}s
            </div>
            <div>
              <span className="text-slate-500 block text-[9px] uppercase font-sans font-bold">Total de Frases / Paras</span>
              <span className="text-purple-400 font-bold">{phraseBegins.length} frases • {paras.length} paras</span>
            </div>
          </div>
        )}

        {/* MAIN BODY: Tale.fyi Reader Stage */}
        <div 
          ref={readerContainerRef}
          onClick={onParagrafoClick}
          className="tale-reader-content flex-1 min-h-0 overflow-y-auto my-3 py-8 px-4 sm:px-8 space-y-4 select-none [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-700/50 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-500/70"
          style={{ 
            fontSize: `${fontSize}px`,
            fontFamily: "Georgia, 'Times New Roman', serif",
            cursor: 'pointer'
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(processedHtml) }}
        />

        {/* Playback Controls Footer */}
        <div className="space-y-3 pt-2 border-t border-white/10 shrink-0">
          {/* Seekbar */}
          <div className="space-y-1">
            <input
              type="range"
              min={0}
              max={duration || 100}
              step={0.1}
              value={currentTime}
              onChange={handleSeek}
              className="w-full h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer accent-amber-500"
            />
            <div className="flex justify-between text-[10px] text-white/50 font-bold font-mono">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Controls Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => skipSeconds(-10)}
                className="p-2 text-white/70 hover:text-white transition-transform active:scale-95"
                title="-10s"
              >
                <RotateCcw size={20} />
              </button>

              <button
                onClick={togglePlayPause}
                className="w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 flex items-center justify-center hover:scale-105 active:scale-95 transition-all cursor-pointer"
              >
                {isPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} className="ml-0.5" fill="currentColor" />}
              </button>

              <button
                onClick={() => skipSeconds(10)}
                className="p-2 text-white/70 hover:text-white transition-transform active:scale-95"
                title="+10s"
              >
                <RotateCw size={20} />
              </button>
            </div>

            <div className="flex items-center gap-3">
              {/* Speed Button */}
              <button
                onClick={cycleSpeed}
                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-xl text-xs font-medium text-amber-300 tracking-wider transition-all"
                title="Velocidade"
              >
                {currentSpeed}x
              </button>

              {/* Volume Slider */}
              <div className="relative">
                <button
                  onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                  className="p-2 text-white/70 hover:text-white"
                  title="Volume"
                >
                  {isMuted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                {showVolumeSlider && (
                  <div className="absolute bottom-10 right-0 bg-slate-900 border border-white/10 p-3 rounded-2xl flex flex-col items-center gap-2 z-50">
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={isMuted ? 0 : volume}
                      onChange={(e) => {
                        setVolume(parseFloat(e.target.value));
                        setIsMuted(false);
                      }}
                      className="w-24 h-1 bg-white/20 rounded accent-amber-500 cursor-pointer"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
